"use client";

import { useEffect, useState } from "react";
import { Mail } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

type NotifyExplainerModalProps = {
  open: boolean;
  loading?: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel: (dontShowAgain: boolean) => void | Promise<void>;
  onConfirm: (dontShowAgain: boolean) => void | Promise<void>;
};

export function NotifyExplainerModal({
  open,
  loading = false,
  onOpenChange,
  onCancel,
  onConfirm,
}: NotifyExplainerModalProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    if (!open) setDontShowAgain(false);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[340px] gap-0 overflow-hidden rounded-xl border border-white/10 bg-zinc-950 p-0 text-white shadow-[0_24px_80px_rgba(0,0,0,0.55)]">
        <div className="px-5 pb-5 pt-5">
          <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/[0.03]">
            <Mail className="h-5 w-5 text-white/80" />
          </div>

          <DialogHeader className="space-y-0 text-center">
            <DialogTitle className="text-[17px] font-semibold tracking-tight text-white">
              Receive email when chat finishes?
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4 text-[13px] leading-6 text-white">
            <ul className="list-disc space-y-1.5 pl-5 text-left">
              <li>Only applies to this chat.</li>
              <li>You can switch tabs or leave the page.</li>
              <li>If you close the tab or browser, the chat will keep running.</li>
              <li>We will email you when it finishes.</li>
            </ul>
          </div>

          <label className="mt-5 flex cursor-pointer items-center gap-3 text-sm font-medium text-white">
            <Checkbox
              checked={dontShowAgain}
              onCheckedChange={(checked) => setDontShowAgain(checked === true)}
              className="h-4 w-4 rounded-[4px] border-white/30 bg-transparent text-white data-[state=checked]:border-white data-[state=checked]:bg-transparent data-[state=checked]:text-white"
            />
            <span>Don&apos;t show again</span>
          </label>

          <DialogFooter className="mt-5 flex-row items-center justify-between sm:justify-between sm:space-x-0">
            <Button
              type="button"
              variant="ghost"
              disabled={loading}
              onClick={() => void onCancel(dontShowAgain)}
              className="h-9 rounded-md border border-white/12 bg-transparent px-3 text-[13px] text-white hover:bg-white/[0.04] hover:text-white"
            >
              Not now
            </Button>
            <Button
              type="button"
              disabled={loading}
              onClick={() => void onConfirm(dontShowAgain)}
              className="h-9 rounded-md bg-white px-3 text-[13px] text-black hover:bg-zinc-200"
            >
              Turn on for this chat
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
